import React from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';

import { Button } from '@/components/atoms/Button';
import { Typography } from '@/components/atoms/Typography';
import { EyeIcon } from '@/components/atoms/icons';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';

export interface IdProofCardWebProps {
    title: string;
    value: string;
    onView?: () => void;
    onUpdate?: () => void;
    /**
     * When true, show a secondary "Update" action next to "View"
     * (used on Account & Security web screen).
     */
    showUpdate?: boolean;
    /**
     * Optional container style override for layout (e.g. flex: 1 in a row).
     */
    containerStyle?: ViewStyle;
}

/**
 * Reusable ID proof card (Passport / Emirates ID).
 * Extracted from the mobile id-proof screen and reused on web Account & Security.
 */
export function IdProofCardWeb({
    title,
    value,
    onView,
    onUpdate,
    showUpdate,
    containerStyle,
}: IdProofCardWebProps) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();

    return (
        <View style={[styles.card, { backgroundColor: colors.backgroundLevel2 }, containerStyle]}>
            <Typography variant="headline" color="primary" style={[styles.label, { textAlign: isRTL ? 'right' : 'left' }]}>
                {title}
            </Typography>

            <View style={[styles.inputContainer, { borderColor: colors.outlineInactive }]}>
                <Typography variant="body" color="primary" style={[styles.inputText, { textAlign: isRTL ? 'right' : 'left' }]}>
                    {value}
                </Typography>
            </View>

            <View style={styles.actionsContainer}>
                {onView && (
                    <Button
                        variant="text"
                        title={t('profile.view')}
                        leading={<EyeIcon size={18} color={colors.buttonDefault} />}
                        onPress={onView}
                        style={styles.actionButton}
                    />
                )}
                {showUpdate && onUpdate && (
                    <Button
                        variant="text"
                        title={t('profile.update' as any) || 'Update'}
                        onPress={onUpdate}
                        style={styles.actionButton}
                    />
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
        gap: 8,
    },
    label: {
        marginBottom: 0,
    },
    inputContainer: {
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 16,
        minHeight: 50,
        justifyContent: 'center',
    },
    inputText: {
        fontFamily: 'Matter',
        fontWeight: '400',
    },
    actionsContainer: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 8,
    },
    actionButton: {
        flex: 1,
    },
});

export default IdProofCardWeb;


